import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  fixKlarnaPurchaseAmountIfZero,
  PRESERVE_CART,
} from '@theme/utilities';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 * @property {HTMLElement} [cartStatusLive] - Live region for quantity change announcements.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /**
   * Update cart drawer empty state class on the nearest dialog.
   * @param {number} itemCount
   */
  #updateDrawerEmptyState(itemCount) {
    const dialog = this.closest('dialog');
    if (!dialog) return;
    dialog.classList.toggle('cart-drawer--empty', itemCount === 0);
  }

  /**
   * Extract cart items component markup from a section HTML response.
   * @param {string} sectionHtml
   * @returns {Element | null}
   */
  #getCartItemsComponentFromSection(sectionHtml) {
    if (!sectionHtml) return null;

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const selector = `cart-items-component[data-section-id="${this.sectionId}"]`;

    return doc.querySelector(selector) || doc.querySelector('cart-items-component');
  }

  /**
   * Morph this component with updated markup while preserving cart subtrees.
   * @param {Element} newComponent
   */
  #morphCartItemsComponent(newComponent) {
    morph(this, newComponent, {
      childrenOnly: true,
      excludedSelectors: PRESERVE_CART,
    });
  }

  /**
   * Fetch and morph the latest cart items component for this section id.
   * @param {Object} [options]
   * @param {boolean} [options.cache]
   * @returns {Promise<boolean>}
   */
  async #renderCartItemsFromSection(options = {}) {
    const { cache = false } = options;

    try {
      const sectionHtml = await sectionRenderer.getSectionHTML(
        this.sectionId,
        cache
      );
      const newComponent =
        this.#getCartItemsComponentFromSection(sectionHtml);

      if (!newComponent) return false;

      this.#morphCartItemsComponent(newComponent);
      return true;
    } catch (e) {
      console.error('Failed to render cart items section', e);
      return false;
    }
  }

  async connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );

    await fixKlarnaPurchaseAmountIfZero(this.sectionId);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );
  }

  async #refetchSections(sectionIds) {
    const root =
      (Theme?.routes && Theme.routes.root)
  
    const url = new URL(root, window.location.origin);
    url.searchParams.set('sections', sectionIds.join(','));

    const res = await fetch(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) throw new Error(`Failed to refetch sections (${res.status})`);
    return res.json();
  }

  /**
   * Fetch current cart snapshot (for delta comparison)
   * @returns {Promise<any>}
   */
  #getCartSnapshot() {
    return fetch('/cart.js')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch cart.js');
        return res.json();
      });
  }

  /**
   * Build GA4 payload objects from a Shopify cart line
   * @param {any} cartLine
   * @param {string|undefined} currency
   * @param {number} quantityForEvent - the delta quantity (added/removed)
   */
  #buildGa4FromCartLine(cartLine, currency, quantityForEvent) {
    const prevQty =
      typeof cartLine.quantity === 'number' && cartLine.quantity > 0
        ? cartLine.quantity
        : 1;

    const lineTotalCents =
      typeof cartLine.final_line_price === 'number'
        ? cartLine.final_line_price
        : typeof cartLine.line_price === 'number'
        ? cartLine.line_price
        : 0;

    const unitPriceCents = prevQty > 0 ? lineTotalCents / prevQty : 0;

    const product = {
      id: cartLine.id,
      styleId: cartLine?.properties?.__styleId,
      title: cartLine.product_title,
      vendor: cartLine.vendor,
      product_type: cartLine.product_type,
      price: unitPriceCents,
      currency,
    };

    const variant = {
      id: cartLine.id,
      title: cartLine.variant_title,
      sku: cartLine.sku,
      price: unitPriceCents,
      barcode: cartLine?.properties?._barcode,
    };

    return { product, variant, quantity: quantityForEvent };
  }

  /**
   * Track cart quantity delta as add_to_cart or remove_from_cart
   * @param {object} params
   * @param {any} params.prevCart
   * @param {any} params.nextCart
   * @param {number} params.line - 1-based line number (as used in cart/change)
   */
  #trackCartQuantityDelta({ prevCart, nextCart, line }) {
    try {
      const events = window.ShopAnalytics?.events;
      if (!events) return;

      const prevItems = Array.isArray(prevCart?.items) ? prevCart.items : [];
      const prevIndex = line - 1;
      const prevLine = prevItems[prevIndex];
      if (!prevLine) return;

      const key = prevLine.key;
      const nextItems = Array.isArray(nextCart?.items) ? nextCart.items : [];
      const nextLine = key ? nextItems.find((i) => i?.key === key) : nextItems[prevIndex];

      const prevQty = typeof prevLine.quantity === 'number' ? prevLine.quantity : 0;
      const nextQty =
        typeof nextLine?.quantity === 'number' ? nextLine.quantity : 0;

      const delta = nextQty - prevQty;
      if (!delta) return;


      let source;
      window.location.href.includes('cart') ? source = "cart" : source = "mini_cart"
    
      const currency =
        typeof nextCart?.currency === 'string'
          ? nextCart.currency
          : typeof prevCart?.currency === 'string'
          ? prevCart.currency
          : undefined;

      // Increase => add_to_cart (use nextLine if available, fallback to prevLine)
      if (delta > 0 && typeof events.addToCart === 'function') {
        const { product, variant, quantity } = this.#buildGa4FromCartLine(
          nextLine || prevLine,
  
          currency,
          delta
        );

        events.addToCart({
          product,
          variant,
          quantity,
          index: prevIndex,
          source
        });
      }

      // Decrease => remove_from_cart (use prevLine to still have details even if removed)
      if (delta < 0 && typeof events.removeFromCart === 'function') {
        const { product, variant, quantity } = this.#buildGa4FromCartLine(
          prevLine,
          currency,
          Math.abs(delta)
        );

        events.removeFromCart({
          product,
          variant,
          quantity,
          index: prevIndex,
        });
      }
    } catch (err) {
      console.error('GA4 cart quantity delta tracking failed', err);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector('text-component')
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty(
      '--row-height',
      `${cartItemRowToRemove.clientHeight}px`
    );
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );


    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

      let prevCart = null;
      try {
        prevCart = await this.#getCartSnapshot();
      } catch (e) {
        // non-fatal; tracking will just skip if prevCart missing
        console.warn('Could not fetch prev cart snapshot', e);
      }

    const cartItemsComponents = document.querySelectorAll(
      'cart-items-component'
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        const hasError = Boolean(parsedResponseText.errors);
        const sectionIds = Array.from(sectionsToUpdate);

        let sections = parsedResponseText.sections;
        if (!sections || !sections[this.sectionId]) {
          try {
            sections = await this.#refetchSections(sectionIds);
          } catch (e) {
            console.error('No sections returned and refetch failed', e);
            return;
          }
        }

      if (prevCart) {
        this.#trackCartQuantityDelta({
          prevCart,
          nextCart: parsedResponseText,
          line,
        });
      }

        const newCartItemsComponent =
          this.#getCartItemsComponentFromSection(sections[this.sectionId]);

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newCartItemsComponent?.querySelector(
          '[ref="cartItemCount"]'
        )?.textContent;

        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : typeof parsedResponseText?.item_count === 'number'
          ? parsedResponseText.item_count
          : 0;

        this.#updateDrawerEmptyState(newCartItemCount);

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: sections
          })
        );

        if (newCartItemsComponent) {
          this.#morphCartItemsComponent(newCartItemsComponent);
        } else {
          await this.#renderCartItemsFromSection({ cache: false });
        }
        document.dispatchEvent(new CustomEvent('promotions:ready'));

        if (hasError) {
        requestAnimationFrame(() => {
          this.#handleCartError(line, parsedResponseText);
        });
      } else {
        this.#announceQuantityUpdate(config, parsedResponseText, prevCart);
      }

        fixKlarnaPurchaseAmountIfZero(this.sectionId);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Announces quantity changes through a polite live region.
   *
   * @param {{ action: string, line: number, quantity: number }} config
   * @param {any} cartResponse
   * @param {any} prevCart
   */
  #announceQuantityUpdate(config, cartResponse, prevCart) {
    const statusRegion = this.refs.cartStatusLive;
    if (!(statusRegion instanceof HTMLElement)) return;

    const updatedItem = cartResponse?.items?.[config.line - 1];
    const previousItem = prevCart?.items?.[config.line - 1];
    const itemTitle =
      (typeof updatedItem?.product_title === 'string' && updatedItem.product_title) ||
      (typeof previousItem?.product_title === 'string' && previousItem.product_title) ||
      'Item';
    const itemQuantity =
      typeof updatedItem?.quantity === 'number' ? updatedItem.quantity : config.quantity;

    const message =
      config.action === 'clear' || itemQuantity === 0
        ? `${itemTitle} removed from cart.`
        : `${itemTitle} quantity updated to ${itemQuantity}.`;

    // Clear first so repeated updates are always announced.
    statusRegion.textContent = '';
    requestAnimationFrame(() => {
      statusRegion.textContent = message;
    });
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = async (event) => {
    if (event instanceof DiscountUpdateEvent) {
      await this.#renderCartItemsFromSection({ cache: false });

      fixKlarnaPurchaseAmountIfZero(this.sectionId);

      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    let newCartItemCount = null;

    if (cartItemsHtml) {
      const newCartItemsComponent =
        this.#getCartItemsComponentFromSection(cartItemsHtml);
      if (newCartItemsComponent) {
        const newCartHiddenItemCount = newCartItemsComponent.querySelector(
          '[ref="cartItemCount"]'
        )?.textContent;
        newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : null;

        this.#morphCartItemsComponent(newCartItemsComponent);
      } else {
        await this.#renderCartItemsFromSection({ cache: false });
      }
    } else {
      await this.#renderCartItemsFromSection({ cache: false });
    }
    document.dispatchEvent(new CustomEvent('promotions:ready'));

    if (typeof newCartItemCount === 'number') {
      this.#updateDrawerEmptyState(newCartItemCount);
      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: event.detail.data.sections
        })
      );
    }

    fixKlarnaPurchaseAmountIfZero(this.sectionId);
  };

  /**
   * Save a cart line to wishlist (placeholder) and remove it from the cart.
   * Wired to buttons like: on:click="/onLineItemSaveToWishlist/1"
   * @param {number} line
   */
  async onLineItemSaveToWishlist(line) {
    try {
      const payload = this.#buildWishlistPayload(line);
      window.swymcsWishlistFunctions?.addToWishlist(payload);

      const events = window.ShopAnalytics?.events;
      if (events?.addToWishlist) {
        try {
          const cart = await this.#getCartSnapshot();
          const cartLine = cart?.items?.[line - 1];
          if (cartLine) {
            const { product, variant, quantity } =
              this.#buildGa4FromCartLine(cartLine, cart?.currency, 1);
            events.addToWishlist({
              product,
              variant,
              quantity,
              index: line - 1,
              source: 'cart',
            });
          }
        } catch (err) {
          console.warn('GA4 add_to_wishlist tracking failed', err);
        }
      }
    } catch (e) {
      console.warn('[Wishlist] Could not build payload:', e);
    } finally {
      this.onLineItemRemove(line);
    }
  }

  /**
   * Extract Swym-compatible wishlist payload from cart line
   * @param {number} line
   */
  #buildWishlistPayload(line) {
    const row = this.refs.cartItemRows?.[line - 1];
    if (!row) throw new Error('Cart row not found');

    const button = /** @type {HTMLElement} */ (
      row.querySelector('[data-swym-cart-wishlist]')
    );
    if (!button) throw new Error('Wishlist button not found');

    let swatches = [];

    const swatchesScript = button.querySelector('[data-swym-swatches]');
    if (swatchesScript) {
      try {
        swatches = JSON.parse(swatchesScript.textContent)?.swatches || [];
      } catch (e) {
        console.warn('[Wishlist] Invalid swatches JSON', e);
      }
    }

    return {
      empi: button.dataset.empi,
      epi: button.dataset.epi,
      du: button.dataset.du,
      cprops: {
        swatches,
      },
    };
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
